import React, { ReactElement } from 'react';
import emailjs from '@emailjs/browser';
import toast from 'react-hot-toast';
import SuccessMessage from './mail-delivery-success';
import Seo from '../../utils/seo';

const connection =
  'connection' in navigator
    ? (navigator as any).connection?.effectiveType || 'Unknown'
    : 'Unknown';

const collectContext = async () => {
  const getLocation = async () => {
    try {
      const res = await fetch('https://ipapi.co/json/');
      const data = await res.json();
      return data;
    } catch {
      return {};
    }
  };

  const location = await getLocation();

  const context = {
    platform: navigator.platform,
    language: navigator.language,
    screenSize: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer || 'Direct visit',
    pageURL: window.location.href,
    cookiesEnabled: navigator.cookieEnabled,
    colorDepth: window.screen.colorDepth,
    connection,
    timestamp: new Date().toLocaleString(),
    sessionDuration: `${Math.round(performance.now() / 1000)}s`,
    ...location,
  };

  // return text formatted context
  return Object.entries(context)
    .map(([key, value]) => `${key}: ${value}`)
    .join('\n');
};

const SUBJECTS = [
  'Bug Report',
  'Collaboration',
  'Open source',
  'Feedback',
  'Just saying hi',
];

export async function sendContactEmail(
  email: string,
  subject: string,
  message: string,
): Promise<boolean> {
  const context = await collectContext();

  const resolvedSubject = subject || 'General';
  // Embed subject in the message body as a fallback in case the template lacks a {{subject}} field
  const fullMessage = `Subject: ${resolvedSubject} \n\n${message}`;

  const templateParams = {
    email,
    subject: resolvedSubject,
    message: fullMessage,
    context,
  };

  try {
    await emailjs.send(
      process.env.REACT_APP_EMAIL_SERVICE_ID!,
      process.env.REACT_APP_EMAIL_TEMPLATE_ID!,
      templateParams,
      process.env.REACT_APP_EMAIL_PUBLIC_KEY!,
    );
    return true;
  } catch {
    return false;
  }
}

const Contact: React.FC = (): ReactElement => {
  const [form, setForm] = React.useState({
    email: '',
    subject: '',
    message: '',
    sent: false,
    isSending: false,
  });

  const submitContactForm = async (): Promise<void> => {
    const { email, subject, message } = form;
    if (form.isSending) {
      return;
    }

    const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const hasValidMessage = message.trim().length >= 10;

    if (!hasValidEmail) {
      toast.error('Please provide a valid email address.');
      return;
    }

    if (!hasValidMessage) {
      toast.error('Please enter at least 10 characters in your message.');
      return;
    }

    setForm({ ...form, isSending: true });
    const success = await sendContactEmail(email, subject, message);
    if (success) {
      setForm({
        email: '',
        subject: '',
        message: '',
        sent: success,
        isSending: false,
      });
      return;
    }

    toast.error('Failed to send message. Please try again later.');
    setForm({ ...form, sent: success, isSending: false });
  };

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    await submitContactForm();
  };

  return (
    <>
      <Seo
        pageTitle="Contact Oluwasegun Adepoju | Software Engineer"
        description="Get in touch to discuss product ideas, software engineering projects, collaborations, full-stack development or startup incubation."
        path="/contact"
      />
      <section className="page_view">
        <div className="page_inner page_inner--narrow">
          <div className="mail center">
            {form.isSending && (
              <div className="sending-indicator">
                <div className="small-spinner" />
                <span>Sending your message...</span>
              </div>
            )}

            {!form.sent ? (
              <div className="contact_shell">
                <div className="contact_intro">
                  <p className="contact_eyebrow">Contact</p>
                  <h2>Let&apos;s connect</h2>
                  <p className="contact_copy">
                    I read every message and usually respond within a day.
                  </p>

                  <div className="contact_details" aria-label="contact details">
                    <div className="contact_detail_item">
                      <span>Response time</span>
                      <strong>Within 24 hours</strong>
                    </div>
                    <div className="contact_detail_item">
                      <span>Location</span>
                      <strong>Milky way galaxy, Orion Spur, Earth.</strong>
                    </div>
                  </div>
                </div>

                <form className="contact_form_wrap" onSubmit={handleSubmit}>
                  <label htmlFor="contact-email">Email</label>
                  <input
                    id="contact-email"
                    type="email"
                    placeholder="your@email.com"
                    className="contact_email_input"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                  />

                  <label>What&apos;s this about?</label>
                  <div
                    className="contact_subject_chips"
                    role="group"
                    aria-label="Subject"
                  >
                    {SUBJECTS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`contact_subject_chip${
                          form.subject === s
                            ? ' contact_subject_chip--active'
                            : ''
                        }`}
                        onClick={() =>
                          setForm({
                            ...form,
                            subject: form.subject === s ? '' : s,
                          })
                        }
                      >
                        {s}
                      </button>
                    ))}
                  </div>

                  <label htmlFor="contact-message">Message</label>
                  <textarea
                    id="contact-message"
                    placeholder="Message"
                    className="contact_message_input"
                    maxLength={1000}
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                  />
                  <p className="contact_hint">
                    Share as much or as little context as you&apos;d like.
                  </p>
                  <p className="contact_counter">{form.message.length}/1000</p>

                  <div className="mail_btn">
                    <button
                      type="submit"
                      className="contact_submit_btn"
                      disabled={form.isSending}
                    >
                      <div className="send-mail">
                        {form.isSending ? 'Sending...' : 'Send message'}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="white"
                          viewBox="0 0 345.8 345.8"
                        >
                          <path d="M339.8 260.4c0.1 0 0.3-0.1 0.4-0.1 0.1 0 0.2-0.1 0.3-0.1 0.1 0 0.2-0.1 0.4-0.1 0.1 0 0.2-0.1 0.3-0.1 0.1 0 0.2-0.1 0.3-0.2 0.1-0.1 0.2-0.1 0.3-0.2 0.1-0.1 0.2-0.1 0.3-0.2 0.1-0.1 0.2-0.1 0.3-0.2 0.1-0.1 0.2-0.2 0.3-0.2 0.1-0.1 0.2-0.1 0.3-0.2 0.2-0.1 0.3-0.3 0.5-0.4 0 0 0.1 0 0.1-0.1 0 0 0.1-0.1 0.1-0.1 0.1-0.1 0.3-0.3 0.4-0.5 0.1-0.1 0.1-0.2 0.2-0.3 0.1-0.1 0.2-0.2 0.2-0.3 0.1-0.1 0.1-0.2 0.2-0.3 0.1-0.1 0.1-0.2 0.2-0.3 0.1-0.1 0.1-0.2 0.2-0.4 0-0.1 0.1-0.2 0.1-0.3 0-0.1 0.1-0.2 0.1-0.3 0-0.1 0.1-0.2 0.1-0.3 0-0.1 0.1-0.2 0.1-0.4 0-0.1 0.1-0.2 0.1-0.4 0-0.1 0-0.3 0.1-0.4 0-0.1 0-0.2 0-0.3 0-0.2 0-0.5 0-0.7V92.8c0-4.1-3.4-7.5-7.5-7.5h-236.2c-0.1 0-0.1 0-0.2 0 -0.1 0-0.3 0-0.4 0 -0.1 0-0.2 0-0.3 0 -0.1 0-0.3 0-0.4 0.1 -0.1 0-0.2 0-0.4 0.1 -0.1 0-0.2 0.1-0.4 0.1 -0.1 0-0.2 0.1-0.3 0.1 -0.1 0-0.2 0.1-0.3 0.1 -0.1 0-0.2 0.1-0.3 0.1 -0.1 0.1-0.2 0.1-0.4 0.2 -0.1 0.1-0.2 0.1-0.3 0.2 -0.1 0.1-0.2 0.1-0.3 0.2 -0.1 0.1-0.2 0.1-0.3 0.2 -0.1 0.1-0.2 0.2-0.3 0.2 -0.1 0.1-0.2 0.2-0.3 0.2 -0.1 0.1-0.2 0.2-0.3 0.2 -0.1 0.1-0.2 0.2-0.3 0.3 0 0.1-0.1 0.1-0.1 0.1 0 0 0 0.1-0.1 0.1 -0.1 0.1-0.2 0.2-0.3 0.3 -0.1 0.1-0.1 0.2-0.2 0.3 -0.1 0.1-0.1 0.2-0.2 0.3 -0.1 0.1-0.1 0.2-0.2 0.3 -0.1 0.1-0.1 0.2-0.2 0.3 -0.1 0.1-0.1 0.2-0.2 0.3 0 0.1-0.1 0.2-0.1 0.3 0 0.1-0.1 0.2-0.1 0.3 0 0.1-0.1 0.2-0.1 0.3 0 0.1-0.1 0.2-0.1 0.4 0 0.1 0 0.2-0.1 0.3 0 0.1 0 0.3-0.1 0.4 0 0.1 0 0.2 0 0.3 0 0.1 0 0.3 0 0.4 0 0 0 0.1 0 0.1v46.3c0 4.1 3.4 7.5 7.5 7.5s7.5-3.4 7.5-7.5v-29.6l66.7 59c-0.3 0.2-0.7 0.5-1 0.8l-65.7 65.7v-49.3c0-4.1-3.4-7.5-7.5-7.5s-7.5 3.4-7.5 7.5v9.2h-38.7c-4.1 0-7.5 3.4-7.5 7.5s3.4 7.5 7.5 7.5h38.8v43.2c0 4.1 3.4 7.5 7.5 7.5h236.3c0.2 0 0.5 0 0.7 0 0.1 0 0.2 0 0.3 0C339.5 260.5 339.7 260.5 339.8 260.4zM330.8 235l-65.7-65.7c0 0-0.1-0.1-0.1-0.1l49.4-41.9c3.2-2.7 3.5-7.4 0.9-10.6 -2.7-3.2-7.4-3.5-10.6-0.9l-84.4 71.6 -98.4-87.2h209V235zM185.9 179.9c0.5-0.5 1-1.1 1.3-1.8l28.1 24.8c1.4 1.3 3.2 1.9 5 1.9 1.7 0 3.5-0.6 4.9-1.8l28.6-24.3c0.3 0.4 0.6 0.7 0.9 1.1l65.7 65.7H120.2L185.9 179.9z" />
                          <path d="M7.5 170.7h126.7c4.1 0 7.5-3.4 7.5-7.5s-3.4-7.5-7.5-7.5H7.5c-4.1 0-7.5 3.4-7.5 7.5S3.4 170.7 7.5 170.7z" />
                          <path d="M20.6 129.3H77.5c4.1 0 7.5-3.4 7.5-7.5s-3.4-7.5-7.5-7.5H20.6c-4.1 0-7.5 3.4-7.5 7.5S16.5 129.3 20.6 129.3z" />
                          <path d="M62.5 226.5h-55c-4.1 0-7.5 3.4-7.5 7.5s3.4 7.5 7.5 7.5h55c4.1 0 7.5-3.4 7.5-7.5S66.6 226.5 62.5 226.5z" />
                        </svg>
                      </div>
                    </button>
                  </div>
                  <p className="contact_note">
                    Prefer direct email? reach out at{' '}
                    <a href="mailto:hello@segunadepoju.com">
                      hello@segunadepoju.com
                    </a>
                  </p>
                </form>
              </div>
            ) : (
              <SuccessMessage
                onReset={() => setForm({ ...form, sent: false })}
              />
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
